import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { useAdmin } from '../hooks/useAdmin';
import { INSTITUCION_SEMAR } from '../config/institutions';
import Notifications from './Notifications';

/* ── Estilos en línea del sistema Tactical Navy UI ── */
const S = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.65)',
    backdropFilter: 'blur(4px)',
    zIndex: 990,
    animation: 'fadeInOverlay 0.35s forwards',
  },
  aside: (isMobile, isOpen) => ({
    width: isMobile ? '260px' : '210px',
    maxWidth: isMobile ? '260px' : '210px',
    height: isMobile ? 'calc(100dvh - 64px - env(safe-area-inset-bottom, 0px))' : '100vh',
    position: 'fixed',
    top: 0, left: 0,
    zIndex: 1000,
    overflowY: 'auto',
    overflowX: 'hidden',
    transform: isMobile ? (isOpen ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)',
    transition: 'transform 0.4s cubic-bezier(0.19,1,0.22,1)',
    /* Fondo naval sólido con borde dorado derecho */
    background: 'linear-gradient(180deg, #0d2035 0%, #0a1a2a 60%, #071526 100%)',
    borderRight: '1px solid rgba(197,160,89,0.3)',
    boxShadow: '4px 0 24px rgba(0,0,0,0.6), inset -1px 0 0 rgba(197,160,89,0.1)',
    display: 'flex',
    flexDirection: 'column',
  }),
  logo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '24px 16px 20px',
    borderBottom: '1px solid rgba(197,160,89,0.2)',
    position: 'relative',
  },
  logoIcon: {
    fontSize: '2rem',
    filter: 'drop-shadow(0 0 8px rgba(197,160,89,0.6))',
    marginBottom: '6px',
  },
  logoTitle: {
    fontFamily: "'Orbitron', sans-serif",
    fontSize: '0.65rem',
    fontWeight: 700,
    color: '#c5a059',
    letterSpacing: '3px',
    textTransform: 'uppercase',
    textAlign: 'center',
    lineHeight: 1.3,
  },
  logoSub: {
    fontFamily: 'var(--font-primary)',
    fontSize: '0.55rem',
    color: 'rgba(197,160,89,0.55)',
    letterSpacing: '1.5px',
    marginTop: '3px',
    textTransform: 'uppercase',
  },
  divider: {
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(197,160,89,0.3), transparent)',
    margin: '6px 16px',
  },
  sectionLabel: {
    fontFamily: "'Orbitron', sans-serif",
    fontSize: '0.55rem',
    color: 'rgba(197,160,89,0.45)',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    padding: '10px 18px 4px',
  },
  nav: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '8px 10px',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  navLink: (isActive) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '10px',
    padding: '9px 12px',
    borderRadius: '6px',
    textDecoration: 'none',
    transition: 'background-color 0.25s ease, color 0.25s ease, border-color 0.25s ease, transform 0.25s ease',
    color: isActive ? '#c5a059' : 'rgba(181,200,216,0.85)',
    background: isActive ? 'rgba(197,160,89,0.1)' : 'transparent',
    border: `1px solid ${isActive ? 'rgba(197,160,89,0.25)' : 'transparent'}`,
    borderLeft: isActive ? '3px solid #c5a059' : '3px solid transparent',
    position: 'relative',
    overflow: 'hidden',
  }),
  navIcon: {
    fontSize: '1.1rem',
    minWidth: '22px',
    textAlign: 'center',
    lineHeight: 1,
  },
  navLabel: (isActive) => ({
    fontFamily: isActive ? 'var(--font-tactical)' : 'var(--font-primary)',
    fontSize: isActive ? '0.7rem' : '0.8rem',
    fontWeight: isActive ? 700 : 500,
    letterSpacing: isActive ? '1px' : '0.3px',
    textTransform: isActive ? 'uppercase' : 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
  adminLink: (isActive) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '9px 12px',
    borderRadius: '6px',
    textDecoration: 'none',
    transition: 'background-color 0.25s ease, color 0.25s ease, border-color 0.25s ease, transform 0.25s ease',
    color: isActive ? '#ff6b35' : 'rgba(255,107,53,0.75)',
    background: isActive ? 'rgba(255,107,53,0.12)' : 'rgba(255,107,53,0.04)',
    border: `1px solid ${isActive ? 'rgba(255,107,53,0.3)' : 'rgba(255,107,53,0.1)'}`,
    borderLeft: `3px solid ${isActive ? '#ff6b35' : 'rgba(255,107,53,0.4)'}`,
    margin: '0 0 4px',
  }),
  footer: {
    padding: '12px 10px',
    borderTop: '1px solid rgba(197,160,89,0.15)',
  },
  legalLink: (isActive) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    borderRadius: '6px',
    textDecoration: 'none',
    color: isActive ? '#c5a059' : 'rgba(181,200,216,0.55)',
    background: isActive ? 'rgba(197,160,89,0.08)' : 'transparent',
    border: `1px solid ${isActive ? 'rgba(197,160,89,0.2)' : 'transparent'}`,
    fontSize: '0.75rem',
    transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease',
    marginBottom: '6px',
  }),
  logoutBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '9px 12px',
    borderRadius: '6px',
    border: '1px solid rgba(255,82,82,0.2)',
    backgroundImage: 'linear-gradient(to right, rgba(255,82,82,0.10), transparent 45%)',
    background: 'rgba(255,82,82,0.05)',
    color: '#ff5252',
    cursor: 'pointer',
    width: '100%',
    transition: 'background-color 0.25s ease, color 0.25s ease, border-color 0.25s ease',
    fontFamily: "'Orbitron', sans-serif",
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  /* Botón hamburguesa mobile */
  hamburger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'fixed',
    top: 'calc(14px + env(safe-area-inset-top, 0px))', left: '14px',
    zIndex: 1010,
    background: '#c5a059',
    border: '1px solid rgba(197,160,89,0.5)',
    borderRadius: '6px',
    width: '44px', height: '44px',
    cursor: 'pointer',
    boxShadow: '0 0 14px rgba(197,160,89,0.4)',
    transition: 'background-color 0.25s ease, transform 0.25s ease',
    flexDirection: 'column',
    gap: '5px',
    padding: '10px',
  },
  hamburgerLine: {
    width: '100%', height: '2px',
    background: '#0a1a2a',
    borderRadius: '2px',
    transition: 'transform 0.25s ease, opacity 0.25s ease',
  },
  notifWrapper: {
    position: 'fixed',
    top: 'calc(14px + env(safe-area-inset-top, 0px))', right: '14px',
    zIndex: 1010,
  },
};

/* ── Mapa de navegación ── */
const NAV_ITEMS = [
  // Perfil primero: es lo primero que el usuario debe revisar al entrar.
  { to: '/dashboard/perfil',        icon: '🎖️', label: 'Perfil' },
  { to: '/dashboard',               icon: '🚢', label: 'Inicio',        end: true },
  { to: '/dashboard/flashcards',     icon: '📇', label: 'Flashcards' },
  { to: '/dashboard/mapa-debilidades', icon: '📊', label: 'Debilidades' },
  { to: '/dashboard/modo-guardia',   icon: '🛡️', label: 'Modo Guardia' },
  // Vortex IA permanece implementado para futura reactivación; sólo admins acceden mediante su panel.
  { to: '/dashboard/voz-vortex',    icon: '🎙️', label: 'Voz Vortex' },
  { to: '/dashboard/audios',        icon: '🎧', label: 'Audios' },
  { to: '/dashboard/study-progress',icon: '🧭', label: 'Avance' },
  { to: '/dashboard/notificaciones',icon: '🚀', label: 'Avisos' },
  { to: '/dashboard/bibliografia',  icon: '📂', label: 'Biblioteca' },
  { to: '/dashboard/quiz',          icon: '📋', label: 'Cuestionarios' },
  { to: '/dashboard/material',      icon: '📒', label: 'Estudio' },
  { to: '/dashboard/plan-estudio',  icon: '🗂️', label: 'Plan Estudio' },
  { to: '/dashboard/vault',         icon: '🛡️', label: 'Bóveda',       semarOnly: true },
  // El generador queda reservado a administración; su implementación no se elimina.
  { to: '/dashboard/simulador',     icon: '🔭', label: 'Simulador' },
  { to: '/dashboard/tutorials',     icon: '🎞️', label: 'Tutoriales' },
  { to: '/dashboard/configuracion', icon: '⚙️', label: 'Configuración' },
];

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const { user, institucion, profileLoading } = useAuth();
  const { isAdmin } = useAdmin(user);

  const handleLogout = async () => {
    try { await signOut(auth); }
    catch (e) { console.error('Error al cerrar sesión', e); }
    finally { navigate('/'); }
  };

  const close = () => { if (isMobile) setIsOpen(false); };

  // Identidad dinámica por institución
  const inst = institucion;
  const showSemarOnly = !profileLoading && institucion === INSTITUCION_SEMAR;
  let logoIcon = '⚓';
  let logoTitle = 'VORTEX\nSEMAR';
  let logoSub = 'Honor · Deber · Estudio';

  if (inst === 'GUARDIA_NACIONAL') {
    logoIcon = '🛡️';
    logoTitle = 'VORTEX\nGUARDIA';
    logoSub = 'Justicia y Paz';
  } else if (inst === 'SEDENA') {
    logoIcon = '⭐';
    logoTitle = 'VORTEX\nSEDENA';
    logoSub = 'Por la Patria';
  }

  return (
    <>
      {/* Overlay mobile */}
      {isOpen && isMobile && (
        <div style={S.overlay} onClick={() => setIsOpen(false)} aria-hidden="true" />
      )}

      {/* ── Sidebar ── */}
      <aside id="vortex-sidebar" style={S.aside(isMobile, isOpen)}>

        {/* Logo / Branding */}
        <div style={S.logo}>
          <span style={S.logoIcon}>{logoIcon}</span>
          <span style={S.logoTitle}>{logoTitle}</span>
          <span style={S.logoSub}>{logoSub}</span>
        </div>

        {/* Navegación principal */}
        <nav style={S.nav} aria-label="Navegación principal">
          {/* Admin (sólo visible para admins) */}
          {isAdmin && (
            <>
              <span style={S.sectionLabel}>Administración</span>
              <NavLink
                to="/dashboard/admin"
                onClick={close}
                style={({ isActive }) => S.adminLink(isActive)}
              >
                <span style={S.navIcon}>🛠️</span>
                <span style={{ fontFamily: "'Orbitron',sans-serif", fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px' }}>ADMIN</span>
              </NavLink>
              <NavLink
                to="/dashboard/admin/cursos"
                onClick={close}
                style={({ isActive }) => S.navLink(isActive)}
              >
                <span style={S.navIcon}>🎓</span>
                <span style={S.navLabel(false)}>Academia Vortex</span>
              </NavLink>
              <NavLink
                to="/dashboard/admin/lector-temas"
                onClick={close}
                style={({ isActive }) => S.navLink(isActive)}
              >
                <span style={S.navIcon}>📖</span>
                <span style={S.navLabel(false)}>Lector de Temas</span>
              </NavLink>
              <NavLink
                to="/dashboard/admin/study-material"
                onClick={close}
                style={({ isActive }) => S.navLink(isActive)}
              >
                <span style={S.navIcon}>📚</span>
                <span style={S.navLabel(false)}>Mat. Estudio</span>
              </NavLink>
              <div style={S.divider} />
            </>
          )}

          <span style={S.sectionLabel}>Módulos</span>

          {NAV_ITEMS.filter((item) => !item.semarOnly || showSemarOnly).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={close}
              style={({ isActive }) => S.navLink(isActive)}
            >
              <span style={S.navIcon}>{item.icon}</span>
              <span style={S.navLabel(false)}>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Footer del sidebar */}
        <div style={S.footer}>
          <div style={S.divider} />
          <button style={S.logoutBtn} onClick={handleLogout}>
            <span style={{ fontSize: '1rem' }}>🚪</span>
            Cerrar Sesión
          </button>
        </div>
      </aside>

      {/* Hamburguesa (mobile) */}
      {isMobile && (
        <button
          type="button"
          style={S.hamburger}
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={isOpen}
          aria-controls="vortex-sidebar"
          aria-haspopup="true"
        >
          <span style={{
            ...S.hamburgerLine,
            transform: isOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none',
          }} />
          <span style={{
            ...S.hamburgerLine,
            opacity: isOpen ? 0 : 1,
          }} />
          <span style={{
            ...S.hamburgerLine,
            transform: isOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none',
          }} />
        </button>
      )}

      {/* Notificaciones (esquina superior derecha) */}
      <div style={S.notifWrapper}>
        <Notifications />
      </div>
    </>
  );
};

export default Sidebar;
